import requestDeliveryQuoteGql from "@/Apollo/mutation/requestDeliveryQuote.graphql";
import myDeliveryQuoteGql from "@/Apollo/query/product/myDeliveryQuote.graphql";
import {
  MyDeliveryQuoteQuery,
  MyDeliveryQuoteQueryVariables,
  RequestDeliveryQuoteInput,
  RequestDeliveryQuoteMutation,
  RequestDeliveryQuoteMutationVariables,
} from "@/Apollo/schema";
import { useDisplayCurrency } from "@/features/display-currency/useDisplayCurrency";
import { useMutation } from "@apollo/client";

/**
 * WHO-6509: asks for a US delivery quote (WHO-6499). The answer is the quote the product page
 * shows next, so it is written straight into `myDeliveryQuote` rather than refetched.
 */
export const useRequestDeliveryQuote = (productId: string) => {
  const currency = useDisplayCurrency();

  const [mutate, result] = useMutation<
    RequestDeliveryQuoteMutation,
    RequestDeliveryQuoteMutationVariables
  >(requestDeliveryQuoteGql, {
    update: (cache, { data }) => {
      const quote = data?.requestDeliveryQuote;

      if (!quote) {
        return;
      }

      cache.writeQuery<MyDeliveryQuoteQuery, MyDeliveryQuoteQueryVariables>({
        query: myDeliveryQuoteGql,
        variables: { id: productId, currency },
        data: {
          product: {
            __typename: "Product",
            id: productId,
            myDeliveryQuote: quote,
          },
        },
      });
    },
  });

  const request = (input: RequestDeliveryQuoteInput) =>
    mutate({ variables: { input, currency } });

  return [request, result] as const;
};
